import { Box, Typography, Container, Grid, IconButton, Divider, Avatar, Stack, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { Facebook, LinkedIn, Twitter, Instagram, WhatsApp, LocationOn, Email, Phone } from '@mui/icons-material';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { title: 'Home', path: '/' },
    { title: 'About', path: '/about' },
    { title: 'Vision', path: '/vision' },
    { title: 'Media', path: '/media' },
    { title: 'Insights', path: '/insights' },
    { title: 'Contact', path: '/contact' },
  ];

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    window.open('https://wa.me/27720100332', '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ background: '#050505', pt: 10, pb: 5, position: 'relative' }}>
      {/* Decorative top border with gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #C8A96B, transparent)',
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={6}>
          {/* Logo and Description Section */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  src="/images/logo.png"
                  alt="John Asfar Logo"
                  sx={{
                    width: 50,
                    height: 50,
                    border: '2px solid #C8A96B',
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: '#C8A96B',
                    fontWeight: 700,
                    letterSpacing: 2,
                  }}
                >
                  JOHN ASFAR
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#999', lineHeight: 1.8, mb: 2 }}>
                Direct executive communication with no gatekeepers and no barriers.
                Global leadership accessible worldwide.
              </Typography>
              {/* Trust Badge */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#C8A96B', borderRadius: '50%' }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>No Gatekeepers</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#C8A96B', borderRadius: '50%' }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>Direct Access</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#C8A96B', borderRadius: '50%' }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>Global Reach</Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={2}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Typography variant="subtitle1" sx={{ color: '#C8A96B', mb: 3, fontWeight: 600, letterSpacing: 1 }}>
                Quick Links
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0 }}>
                {footerLinks.map((item, index) => (
                  <Box component="li" key={item.title} sx={{ mb: 1.5 }}>
                    <Link
                      to={item.path}
                      style={{ 
                        color: '#999', 
                        textDecoration: 'none', 
                        transition: '0.3s',
                        fontSize: '0.9rem',
                        display: 'inline-block',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#C8A96B';
                        e.target.style.transform = 'translateX(5px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#999';
                        e.target.style.transform = 'translateX(0)';
                      }}
                    >
                      {item.title}
                    </Link>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Contact Info Section - UPDATED */}
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography variant="subtitle1" sx={{ color: '#C8A96B', mb: 3, fontWeight: 600, letterSpacing: 1 }}>
                Contact Info
              </Typography>
              <Stack spacing={2}>
                {/* Email - UPDATED with proper capitalization */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Email sx={{ color: '#C8A96B', fontSize: 18, mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999', wordBreak: 'break-all' }}>
                      John.Asfar@WorldwideCorporation.com
                    </Typography>
                  </Box>
                </Box>

                {/* Phone/WhatsApp - UPDATED with WhatsApp logo next to number */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      '& .phone-number': {
                        color: '#25D366',
                      }
                    }
                  }}
                  onClick={handleWhatsAppClick}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ color: '#C8A96B', fontSize: 18, mt: 0.3 }} />
                    <WhatsApp sx={{ color: '#25D366', fontSize: 16, mt: 0.3 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Phone / WhatsApp
                    </Typography>
                    <Typography 
                      variant="body2" 
                      className="phone-number"
                      sx={{ 
                        color: '#999',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      +27 72 010 0332
                    </Typography>
                  </Box>
                </Box>

                {/* Address - UPDATED with Johannesburg and postal code */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <LocationOn sx={{ color: '#C8A96B', fontSize: 18, mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Executive Office
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999', lineHeight: 1.5 }}>
                      341 Beyers Naudé Drive,
                      <br />
                      Northcliff, <strong style={{ color: '#C8A96B' }}>Johannesburg</strong>,
                      <br />
                      <span style={{ color: '#777' }}>2195</span>, South Africa
                    </Typography>
                    {/* Google Maps Link */}
                    <MuiLink
                      href="https://www.google.com/maps/search/341+Beyers+Naud%C3%A9+Drive,+Northcliff,+Johannesburg,+2195,+South+Africa"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-block',
                        mt: 0.5,
                        color: '#C8A96B',
                        fontSize: '0.7rem',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#D6B57B',
                        },
                      }}
                    >
                      📍 Open in Google Maps
                    </MuiLink>
                  </Box>
                </Box>
              </Stack>
            </motion.div>
          </Grid>

          {/* Connect & Newsletter Section */}
          <Grid item xs={12} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Typography variant="subtitle1" sx={{ color: '#C8A96B', mb: 3, fontWeight: 600, letterSpacing: 1 }}>
                Connect
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                {/* WhatsApp Button - ONLY THIS TAKES TO CHAT */}
                <IconButton
                  onClick={handleWhatsAppClick}
                  sx={{ 
                    color: '#999', 
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { 
                      color: '#25D366',
                      bgcolor: 'rgba(37,211,102,0.1)',
                      transform: 'translateY(-3px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <WhatsApp />
                </IconButton>

                {/* LinkedIn Button */}
                <IconButton
                  href="https://www.linkedin.com/in/john-p-asfar-720583346"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#999', 
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { 
                      color: '#0077B5',
                      bgcolor: 'rgba(0,119,181,0.1)',
                      transform: 'translateY(-3px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <LinkedIn />
                </IconButton>

                {/* X (Twitter) Button */}
                <IconButton
                  href="https://x.com/AsfarJohn31961?s=20"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#999', 
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { 
                      color: '#1DA1F2',
                      bgcolor: 'rgba(29,161,242,0.1)',
                      transform: 'translateY(-3px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Twitter />
                </IconButton>

                {/* Instagram Button */}
                <IconButton
                  href="#"
                  sx={{ 
                    color: '#999', 
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { 
                      color: '#E4405F',
                      bgcolor: 'rgba(228,64,95,0.1)',
                      transform: 'translateY(-3px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Instagram />
                </IconButton>
              </Box>

              {/* Response Guarantee Badge */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(200,169,107,0.1), rgba(200,169,107,0.05))',
                  border: '1px solid rgba(200,169,107,0.2)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: '#C8A96B', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  ⚡ 3-Business Day Response
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Always available. Direct access guaranteed.
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5, borderColor: 'rgba(200,169,107,0.15)' }} />

        {/* Copyright Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Copyright © 2025-{currentYear} - Executive Profile - All Rights Reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="caption" sx={{ color: '#666'}}>Privacy Policy</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Terms of Service</Typography>
            </Box>
          </Stack>
          <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
            Powered by Worldwide Systems, LLC
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;